/**
 * TUVI - Proveedores con Sistema de Retenciones
 * Gestión de proveedores, invoices y retenciones tributarias
 */

import React, { useState, useEffect } from 'react';
import ModalDescuentoProveedor from './ModalDescuentoProveedor';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL } from '../../config';
import * as XLSX from 'xlsx';
import './Proveedores.css';
import { matchesAnyField } from '../../utils/searchHelper';

const Proveedores = ({ onVerDetalle }) => {
  // Descuento pactado con el proveedor: se registra contra un capitulo y le baja
  // el gasto a ese capitulo desde que queda guardado.
  const [modalDescuento, setModalDescuento] = useState(null);
  const { success, error: showError, warning } = useNotification();
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresFiltrados, setProveedoresFiltrados] = useState([]);
  const [tiposRetencion, setTiposRetencion] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroClasificacion, setFiltroClasificacion] = useState('todos');
  const [filtroRetencion, setFiltroRetencion] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarModalBases, setMostrarModalBases] = useState(false);
  const [basesEdicion, setBasesEdicion] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'otro',
    clasificacion: 'proveedor',
    nit: '',
    contacto_nombre: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    banco: '',
    numero_cuenta: '',
    tipo_cuenta: '',
    titular_cuenta: '',
    notas: '',
    activo: true,
    // Campos de retención
    tipo_retencion_id: '',
    porcentaje_retencion: 0,
    base_retencion: 0,
    base_retencion_texto: '',
    regimen_tributario: 'responsable_iva',
    tipo_contribuyente: 'sujeto_retencion'
  });
  const [tiposProveedor, setTiposProveedor] = useState({});

  useEffect(() => {
    cargarTiposProveedor();
    cargarTiposRetencion();
    cargarProveedores();
  }, []);

  const cargarTiposProveedor = async () => {
    try {
      const data = await client.query(`{ tiposProveedor { id codigo nombre color } }`);
      const tiposObj = {};
      (data.tiposProveedor || []).forEach(tipo => {
        tiposObj[tipo.codigo] = { nombre: tipo.nombre, color: tipo.color || '#607d8b' };
      });
      setTiposProveedor(tiposObj);
    } catch (error) {
      console.error('Error al cargar tipos de proveedor:', error);
      setTiposProveedor({
        materiales: { nombre: 'Materiales', color: '#ff9800' },
        mano_obra: { nombre: 'Mano de Obra', color: '#2196f3' },
        servicios: { nombre: 'Servicios', color: '#4caf50' },
        transporte: { nombre: 'Transporte', color: '#9c27b0' },
        equipos: { nombre: 'Equipos', color: '#f44336' },
        otro: { nombre: 'Otro', color: '#607d8b' }
      });
    }
  };

  useEffect(() => {
    cargarProveedores();
  }, [filtroTipo, filtroClasificacion, filtroRetencion]);

  useEffect(() => {
    // Filtrar proveedores por búsqueda (flexible: ignora tildes, mayúsculas, espacios)
    if (busqueda.trim() === '') {
      setProveedoresFiltrados(proveedores);
    } else {
      const filtrados = proveedores.filter(p =>
        matchesAnyField(p, ['nombre', 'nit', 'contacto_nombre', 'telefono', 'email', 'ciudad', 'banco', 'tipo_retencion_nombre'], busqueda)
      );
      setProveedoresFiltrados(filtrados);
    }
  }, [busqueda, proveedores]);

  const cargarTiposRetencion = async () => {
    try {
      const data = await client.query(`{ tiposRetencion { id codigo nombre descripcion porcentaje_comun base_comun cantidad_proveedores } }`);
      setTiposRetencion(data.tiposRetencion || []);
    } catch (error) {
      console.error('Error al cargar tipos de retención:', error);
    }
  };

  const cargarProveedores = async () => {
    try {
      setLoading(true);
      const vars = {};
      if (filtroTipo !== 'todos') vars.tipo = filtroTipo;
      if (filtroClasificacion !== 'todos') vars.clasificacion = filtroClasificacion;

      const data = await client.query(`
        query($tipo: String, $clasificacion: String) {
          proveedores(tipo: $tipo, clasificacion: $clasificacion) {
            proveedores {
              id nombre tipo clasificacion nit contacto_nombre telefono email
              direccion ciudad banco numero_cuenta tipo_cuenta titular_cuenta notas activo
              regimen_tributario tipo_contribuyente tipo_retencion_id
              porcentaje_retencion base_retencion base_retencion_texto
              retenciones { retencion_id tipo_retencion_id porcentaje_retencion base_retencion base_retencion_texto es_principal tipo_retencion_codigo tipo_retencion_nombre }
              tipo_retencion_codigo tipo_retencion_nombre
              total_facturas facturas_pendientes saldo_total_pendiente
            }
            estadisticas { total_proveedores proveedores_activos total_facturas_pendientes saldo_total_sistema }
          }
        }
      `, vars);

      setProveedores(data.proveedores.proveedores || []);
      setEstadisticas(data.proveedores.estadisticas);
    } catch (err) {
      console.error('Error al cargar proveedores:', err);
      showError('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTipoRetencionChange = (e) => {
    const tipoId = e.target.value;
    const tipoSeleccionado = tiposRetencion.find(t => t.id === parseInt(tipoId));

    setFormData(prev => ({
      ...prev,
      tipo_retencion_id: tipoId || '',
      // Usar valores actuales del backend (sincronizados con actualización masiva)
      porcentaje_retencion: tipoSeleccionado?.porcentaje_comun || getDefaultPorcentaje(tipoSeleccionado?.codigo) || 0,
      base_retencion: tipoSeleccionado?.base_comun || getDefaultBase(tipoSeleccionado?.codigo) || 0
    }));
  };

  // Fallbacks solo si el backend no tiene datos aún
  const getDefaultPorcentaje = (codigo) => {
    const defaults = {
      'IVA_REGIMEN_SIMPLE': 15, 'HONORARIOS': 11, 'COMISIONES': 11,
      'SERVICIOS': 4, 'ARRENDAMIENTOS': 4, 'COMPRA': 2.5, 'CONSTRUCCION': 2
    };
    return defaults[codigo] || 0;
  };

  const getDefaultBase = (codigo) => {
    const defaults = {
      'IVA_REGIMEN_SIMPLE': 100000, 'SERVICIOS': 100000,
      'COMPRA': 498000, 'CONSTRUCCION': 498000
    };
    return defaults[codigo] || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = await client.mutate(`
        mutation($input: CrearProveedorInput!) {
          crearProveedor(input: $input) { success message proveedor_id }
        }
      `, { input: {
        ...formData,
        tipo_retencion_id: formData.tipo_retencion_id ? parseInt(formData.tipo_retencion_id) : null,
        porcentaje_retencion: parseFloat(formData.porcentaje_retencion) || 0,
        base_retencion: parseFloat(formData.base_retencion) || 0,
      } });

      if (data.crearProveedor.success) {
        success('Proveedor creado exitosamente');
        setMostrarFormulario(false);
        setFormData({
          nombre: '', tipo: 'otro', clasificacion: 'proveedor', nit: '', contacto_nombre: '',
          telefono: '', email: '', direccion: '', ciudad: '',
          banco: '', numero_cuenta: '', tipo_cuenta: '', titular_cuenta: '', notas: '', activo: true,
          tipo_retencion_id: '', porcentaje_retencion: 0, base_retencion: 0,
          base_retencion_texto: '', regimen_tributario: 'responsable_iva', tipo_contribuyente: 'sujeto_retencion'
        });
        cargarProveedores();
      } else {
        showError('Error: ' + data.crearProveedor.message);
      }
    } catch (err) {
      console.error('Error al crear proveedor:', err);
      showError('Error al crear proveedor');
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatPorcentaje = (valor) => {
    return parseFloat(valor || 0).toFixed(valor % 1 === 0 ? 0 : 1) + '%';
  };

  const exportarExcel = async () => {
    try {
      // Cargar todas las facturas de todos los proveedores
      const todasFacturas = [];
      for (const proveedor of proveedores) {
        try {
          const fData = await client.query(`query($pid: Int!) { facturas(proveedor_id: $pid) { facturas { id numero_factura monto saldo_pendiente estado fecha_emision fecha_vencimiento proyecto_nombre nombre_cliente } } }`, { pid: proveedor.id });
          const data = { success: true, facturas: fData.facturas.facturas };
          if (data.success && data.facturas) {
            data.facturas.forEach(factura => {
              todasFacturas.push({
                ...factura,
                proveedor_nombre: proveedor.nombre,
                proveedor_nit: proveedor.nit,
                proveedor_tipo: tiposProveedor[proveedor.tipo]?.nombre || 'Otro'
              });
            });
          }
        } catch (e) {
          console.error(`Error cargando facturas de ${proveedor.nombre}:`, e);
        }
      }

      // Hoja 1: Resumen General
      const resumenData = [
        ['RESUMEN GENERAL DE PROVEEDORES - TUVI'],
        ['Fecha de Exportación:', new Date().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })],
        [''],
        ['ESTADÍSTICAS GENERALES'],
        ['Total Proveedores:', proveedores.length],
        ['Proveedores Activos:', estadisticas?.proveedores_activos || 0],
        ['Total Facturas Sistema:', todasFacturas.length],
        ['Facturas Pendientes:', estadisticas?.total_facturas_pendientes || 0],
        ['Saldo Total Pendiente:', estadisticas?.saldo_total_sistema || 0],
        [''],
        ['RESUMEN POR TIPO DE PROVEEDOR'],
        ['Tipo', 'Cantidad', 'Saldo Pendiente'],
      ];

      // Agrupar por tipo de proveedor
      const porTipo = {};
      proveedores.forEach(p => {
        const tipo = tiposProveedor[p.tipo]?.nombre || 'Otro';
        if (!porTipo[tipo]) porTipo[tipo] = { cantidad: 0, saldo: 0 };
        porTipo[tipo].cantidad++;
        porTipo[tipo].saldo += parseFloat(p.saldo_total_pendiente || 0);
      });
      Object.entries(porTipo).forEach(([tipo, datos]) => {
        resumenData.push([tipo, datos.cantidad, datos.saldo]);
      });

      // Hoja 2: Listado de Proveedores
      const proveedoresData = [
        ['LISTADO COMPLETO DE PROVEEDORES'],
        [''],
        [
          'ID', 'Nombre', 'Tipo', 'NIT', 'Régimen Tributario', 'Tipo Contribuyente',
          'Tipo Retención', '% Retención', 'Base Retención',
          'Total Facturas', 'Facturas Pendientes', 'Facturas Vencidas',
          'Saldo Pendiente', 'Contacto', 'Teléfono', 'Email', 'Ciudad',
          'Banco', 'Tipo Cuenta', 'Número Cuenta', 'Estado'
        ]
      ];

      proveedores.forEach(proveedor => {
        const tipoInfo = tiposProveedor[proveedor.tipo] || { nombre: 'Otro' };
        const facturasProv = todasFacturas.filter(f => f.proveedor_nombre === proveedor.nombre);
        const facturasVencidas = facturasProv.filter(f => f.dias_vencido > 0 && f.estado !== 'pagado').length;
        const retencionPrincipal = proveedor.retenciones?.find(r => r.es_principal == 1) || proveedor.retenciones?.[0];

        proveedoresData.push([
          proveedor.id,
          proveedor.nombre || '',
          tipoInfo.nombre,
          proveedor.nit || '',
          { responsable_iva: 'Responsable de IVA', no_responsable_iva: 'No responsable de IVA', gran_contribuyente: 'Gran Contribuyente' }[proveedor.regimen_tributario] || proveedor.regimen_tributario || '',
          { autorretenedor: 'Autorretenedor', sujeto_retencion: 'Sujeto a Retención', regimen_simple: 'Régimen Simple' }[proveedor.tipo_contribuyente] || proveedor.tipo_contribuyente || '',
          retencionPrincipal?.tipo_retencion_nombre || 'Sin configurar',
          retencionPrincipal?.porcentaje_retencion ? `${retencionPrincipal.porcentaje_retencion}%` : '0%',
          retencionPrincipal?.base_retencion || 0,
          facturasProv.length,
          proveedor.facturas_pendientes || 0,
          facturasVencidas,
          proveedor.saldo_total_pendiente || 0,
          proveedor.contacto_nombre || '',
          proveedor.telefono || '',
          proveedor.email || '',
          proveedor.ciudad || '',
          proveedor.banco || '',
          proveedor.tipo_cuenta || '',
          proveedor.numero_cuenta || '',
          proveedor.activo ? 'Activo' : 'Inactivo'
        ]);
      });

      // Hoja 3: Todas las Facturas con detalle
      const facturasData = [
        ['DETALLE DE TODAS LAS FACTURAS'],
        [''],
        [
          'Proveedor', 'NIT', 'Tipo', 'No. Factura', 'Estado',
          'Subtotal', 'IVA', 'Monto Total', 'Ret. Fuente', 'Ret. IVA',
          'Total Retenciones', 'Valor Neto', 'Monto Pagado', 'Saldo Pendiente',
          'Fecha Emisión', 'Fecha Vencimiento', 'Días Vencido', 'Proyecto', 'Descripción'
        ]
      ];

      todasFacturas.forEach(factura => {
        const esVencida = factura.dias_vencido > 0 && factura.estado !== 'pagado';
        let estadoTexto = '';
        const estadoFinal = esVencida ? 'vencido' : factura.estado;
        switch(estadoFinal) {
          case 'pendiente': estadoTexto = 'Pendiente'; break;
          case 'pagado_parcial': estadoTexto = 'Pago Parcial'; break;
          case 'pagado': estadoTexto = 'Pagado'; break;
          case 'vencido': estadoTexto = 'VENCIDA'; break;
          case 'cancelado': estadoTexto = 'Cancelada'; break;
          default: estadoTexto = estadoFinal;
        }
        const totalRet = (parseFloat(factura.retencion_fuente) || 0) + (parseFloat(factura.retencion_iva) || 0);
        const valorNeto = (parseFloat(factura.monto) || 0) - totalRet;

        facturasData.push([
          factura.proveedor_nombre,
          factura.proveedor_nit || '',
          factura.proveedor_tipo,
          factura.numero_factura || '',
          estadoTexto,
          factura.subtotal || 0,
          factura.iva || 0,
          factura.monto || 0,
          factura.retencion_fuente || 0,
          factura.retencion_iva || 0,
          totalRet,
          valorNeto,
          factura.monto_pagado || 0,
          factura.saldo_pendiente || 0,
          factura.fecha_emision ? new Date(factura.fecha_emision).toLocaleDateString('es-CO') : '',
          factura.fecha_vencimiento ? new Date(factura.fecha_vencimiento).toLocaleDateString('es-CO') : '',
          esVencida ? factura.dias_vencido : 0,
          factura.proyecto_nombre || '',
          factura.descripcion || ''
        ]);
      });

      // Hoja 4: Facturas Pendientes de Pago (ordenadas por días vencido)
      const pendientesData = [
        ['FACTURAS PENDIENTES DE PAGO (Ordenadas por urgencia)'],
        [''],
        [
          'Proveedor', 'NIT', 'No. Factura', 'Estado', 'Monto Total',
          'Saldo Pendiente', 'Fecha Vencimiento', 'Días Vencido', 'Proyecto'
        ]
      ];

      const facturasPendientes = todasFacturas.filter(f =>
        f.estado === 'pendiente' || f.estado === 'pagado_parcial' || (f.dias_vencido > 0 && f.estado !== 'pagado')
      ).sort((a, b) => (b.dias_vencido || 0) - (a.dias_vencido || 0));

      facturasPendientes.forEach(factura => {
        const esVencida = factura.dias_vencido > 0;
        pendientesData.push([
          factura.proveedor_nombre,
          factura.proveedor_nit || '',
          factura.numero_factura || '',
          esVencida ? 'VENCIDA' : (factura.estado === 'pagado_parcial' ? 'Parcial' : 'Pendiente'),
          factura.monto || 0,
          factura.saldo_pendiente || 0,
          factura.fecha_vencimiento ? new Date(factura.fecha_vencimiento).toLocaleDateString('es-CO') : '',
          factura.dias_vencido || 0,
          factura.proyecto_nombre || ''
        ]);
      });

      // Crear libro Excel
      const wb = XLSX.utils.book_new();

      const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
      wsResumen['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen General');

      const wsProveedores = XLSX.utils.aoa_to_sheet(proveedoresData);
      wsProveedores['!cols'] = [
        { wch: 6 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 18 },
        { wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 12 },
        { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 25 },
        { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 10 }
      ];
      XLSX.utils.book_append_sheet(wb, wsProveedores, 'Proveedores');

      const wsFacturas = XLSX.utils.aoa_to_sheet(facturasData);
      wsFacturas['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 15 }, { wch: 12 }, { wch: 25 }, { wch: 40 }
      ];
      XLSX.utils.book_append_sheet(wb, wsFacturas, 'Todas las Facturas');

      const wsPendientes = XLSX.utils.aoa_to_sheet(pendientesData);
      wsPendientes['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
        { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, wsPendientes, 'Pendientes de Pago');

      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Proveedores_Completo_${fecha}.xlsx`);

      success('Archivo Excel generado exitosamente');
    } catch (err) {
      console.error('Error al exportar Excel:', err);
      showError('Error al generar el archivo Excel');
    }
  };

  // Abrir modal de actualización masiva de bases
  const abrirModalBases = async () => {
    try {
      const rData = await client.query(`{ tiposRetencion { id codigo nombre porcentaje_comun base_comun cantidad_proveedores } }`);
      const data = { success: true, tipos: rData.tiposRetencion };
      if (data.success) {
        const tiposConBase = data.tipos.map(t => ({
          id: t.id,
          codigo: t.codigo,
          nombre: t.nombre,
          cantidad_proveedores: t.cantidad_proveedores || 0,
          base_actual: t.base_comun || 0,
          base_nueva: '',
          porcentaje: t.porcentaje_comun || 0,
          porcentaje_nuevo: ''
        }));
        setBasesEdicion(tiposConBase);
        setMostrarModalBases(true);
      }
    } catch (err) {
      showError('Error al cargar tipos de retención');
    }
  };

  const handleBaseChange = (tipoId, valor) => {
    setBasesEdicion(prev => prev.map(b =>
      b.id === tipoId ? { ...b, base_nueva: valor } : b
    ));
  };

  const handlePorcentajeChange = (tipoId, valor) => {
    setBasesEdicion(prev => prev.map(b =>
      b.id === tipoId ? { ...b, porcentaje_nuevo: valor } : b
    ));
  };

  const guardarBasesMasivas = async () => {
    // Tomar cambios donde haya nueva base O nuevo porcentaje
    const cambios = basesEdicion
      .filter(b => {
        const baseDif = b.base_nueva !== '' && parseFloat(b.base_nueva) !== parseFloat(b.base_actual);
        const pctDif = b.porcentaje_nuevo !== '' && parseFloat(b.porcentaje_nuevo) !== parseFloat(b.porcentaje);
        return baseDif || pctDif;
      })
      .map(c => {
        const input = { tipo_retencion_id: c.id };
        if (c.base_nueva !== '' && parseFloat(c.base_nueva) !== parseFloat(c.base_actual)) {
          input.base_retencion = parseFloat(c.base_nueva);
        }
        if (c.porcentaje_nuevo !== '' && parseFloat(c.porcentaje_nuevo) !== parseFloat(c.porcentaje)) {
          input.porcentaje_retencion = parseFloat(c.porcentaje_nuevo);
        }
        return input;
      });

    if (cambios.length === 0) {
      warning('No hay cambios para guardar');
      return;
    }

    try {
      const gqlR = await client.mutate(`mutation($a: [BaseRetencionInput!]!) { actualizarBasesRetencion(actualizaciones: $a) { success message } }`, {
        a: cambios
      });
      const data = gqlR.actualizarBasesRetencion;
      if (data.success) {
        success(data.message || 'Actualización masiva aplicada');
        setMostrarModalBases(false);
        cargarProveedores();
      } else {
        showError(data.error || 'Error al actualizar');
      }
    } catch (err) {
      showError('Error al guardar cambios: ' + (err.message || ''));
    }
  };

  if (loading) {
    return (
      <div className="proveedores-loading">
        <div className="loading-spinner"></div>
        <p>Cargando proveedores...</p>
      </div>
    );
  }

  return (
    <div className="proveedores">
      {/* Header */}
      <div className="proveedores-header">
        <div className="header-left">
          <span className="header-icon">🏢</span>
          <div className="header-text">
            <h2>Proveedores - Contratistas y sus Retenciones</h2>
            <p>Gestión de proveedores con configuración tributaria</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-actualizar-bases" onClick={abrirModalBases}>
            Actualizar Bases
          </button>
          <button className="btn-exportar-excel" onClick={exportarExcel}>
            Exportar Excel
          </button>
          <button className="btn-nuevo-proveedor" onClick={() => setMostrarFormulario(!mostrarFormulario)}>
            {mostrarFormulario ? 'Cancelar' : 'Nuevo Proveedor'}
          </button>
        </div>
      </div>

      {/* Formulario de Nuevo Proveedor */}
      {mostrarFormulario && (
        <div className="form-nuevo-proveedor">
          <h3>Nuevo Proveedor</h3>
          <form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="form-section">
              <h4>Información Básica</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre del Proveedor *</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Construcciones XYZ S.A.S"
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Proveedor</label>
                  <select name="tipo" value={formData.tipo} onChange={handleInputChange}>
                    {Object.entries(tiposProveedor).map(([key, info]) => (
                      <option key={key} value={key}>{info.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Clasificación</label>
                  <select name="clasificacion" value={formData.clasificacion} onChange={handleInputChange}>
                    <option value="proveedor">Proveedor</option>
                    <option value="contratista">Contratista</option>
                    <option value="administrativo">Administrativo</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIT</label>
                  <input
                    type="text"
                    name="nit"
                    value={formData.nit}
                    onChange={handleInputChange}
                    placeholder="Ej: 900123456-1"
                  />
                </div>
              </div>
            </div>

            {/* Configuración Tributaria */}
            <div className="form-section form-section-tributaria">
              <h4>Configuración Tributaria</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de Retención</label>
                  <select
                    name="tipo_retencion_id"
                    value={formData.tipo_retencion_id}
                    onChange={handleTipoRetencionChange}
                  >
                    <option value="">Sin configurar</option>
                    {tiposRetencion.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>% Retención</label>
                  <input
                    type="number"
                    name="porcentaje_retencion"
                    value={formData.porcentaje_retencion}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Ej: 2.5"
                  />
                </div>
                <div className="form-group">
                  <label>Base de Retención</label>
                  <input
                    type="number"
                    name="base_retencion"
                    value={formData.base_retencion}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="Ej: 498000"
                  />
                </div>
                <div className="form-group">
                  <label>Régimen Tributario</label>
                  <select
                    name="regimen_tributario"
                    value={formData.regimen_tributario}
                    onChange={handleInputChange}
                  >
                    <option value="responsable_iva">Responsable de IVA</option>
                    <option value="no_responsable_iva">No responsable de IVA</option>
                    <option value="gran_contribuyente">Gran Contribuyente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo de Contribuyente</label>
                  <select
                    name="tipo_contribuyente"
                    value={formData.tipo_contribuyente}
                    onChange={handleInputChange}
                  >
                    <option value="autorretenedor">Autorretenedor</option>
                    <option value="sujeto_retencion">Sujeto a Retención</option>
                    <option value="regimen_simple">Régimen Simple</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contacto */}
            <div className="form-section">
              <h4>Información de Contacto</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Persona de Contacto</label>
                  <input
                    type="text"
                    name="contacto_nombre"
                    value={formData.contacto_nombre}
                    onChange={handleInputChange}
                    placeholder="Nombre del contacto"
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="Ej: 3001234567"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleInputChange}
                    placeholder="Ej: Bogotá"
                  />
                </div>
                <div className="form-group form-group-large">
                  <label>Dirección</label>
                  <input
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleInputChange}
                    placeholder="Dirección completa"
                  />
                </div>
              </div>
            </div>

            {/* Información Bancaria */}
            <div className="form-section">
              <h4>Información Bancaria</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Banco</label>
                  <input
                    type="text"
                    name="banco"
                    value={formData.banco}
                    onChange={handleInputChange}
                    placeholder="Nombre del banco"
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Cuenta</label>
                  <select name="tipo_cuenta" value={formData.tipo_cuenta} onChange={handleInputChange}>
                    <option value="">Seleccionar...</option>
                    <option value="Ahorros">Ahorros</option>
                    <option value="Corriente">Corriente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Número de Cuenta</label>
                  <input
                    type="text"
                    name="numero_cuenta"
                    value={formData.numero_cuenta}
                    onChange={handleInputChange}
                    placeholder="Número de cuenta"
                  />
                </div>
                <div className="form-group">
                  <label>A Nombre de</label>
                  <input
                    type="text"
                    name="titular_cuenta"
                    value={formData.titular_cuenta}
                    onChange={handleInputChange}
                    placeholder="Titular de la cuenta"
                  />
                </div>
              </div>
            </div>

            <button type="submit" className="btn-submit">
              Crear Proveedor
            </button>
          </form>
        </div>
      )}

      {/* Estadísticas */}
      {estadisticas && (
        <div className="proveedores-stats">
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-icon">🏢</span>
              <div className="stat-content">
                <span className="stat-value">{estadisticas.proveedores_activos}</span>
                <span className="stat-label">Proveedores Activos</span>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon">📄</span>
              <div className="stat-content">
                <span className="stat-value">{estadisticas.total_facturas_pendientes}</span>
                <span className="stat-label">Facturas Pendientes</span>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon">💰</span>
              <div className="stat-content">
                <span className="stat-value">{formatMoney(estadisticas.saldo_total_sistema)}</span>
                <span className="stat-label">Saldo Total Pendiente</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros y Búsqueda */}
      <div className="proveedores-filtros">
        <div className="filtros-header">
          <div className="busqueda-container">
            <input
              type="text"
              className="busqueda-input"
              placeholder="Buscar por nombre, NIT, contacto o tipo de retención..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            {busqueda && (
              <button className="btn-limpiar-busqueda" onClick={() => setBusqueda('')}>X</button>
            )}
          </div>
        </div>

        <div className="prov-filtros-row">
          <div className="prov-filtro-grupo">
            <label>Tipo Proveedor:</label>
            <div className="filtros-buttons">
              <button
                className={`filtro-btn ${filtroTipo === 'todos' ? 'active' : ''}`}
                onClick={() => setFiltroTipo('todos')}
              >
                Todos
              </button>
              {Object.entries(tiposProveedor).map(([key, info]) => (
                <button
                  key={key}
                  className={`filtro-btn ${filtroTipo === key ? 'active' : ''}`}
                  onClick={() => setFiltroTipo(key)}
                >
                  {info.nombre}
                </button>
              ))}
            </div>
          </div>

          <div className="prov-filtro-grupo">
            <label>Clasificación:</label>
            <div className="filtros-buttons">
              <button
                className={`filtro-btn ${filtroClasificacion === 'todos' ? 'active' : ''}`}
                onClick={() => setFiltroClasificacion('todos')}
              >
                Todos
              </button>
              <button
                className={`filtro-btn ${filtroClasificacion === 'proveedor' ? 'active' : ''}`}
                onClick={() => setFiltroClasificacion('proveedor')}
              >
                Proveedores
              </button>
              <button
                className={`filtro-btn ${filtroClasificacion === 'contratista' ? 'active' : ''}`}
                onClick={() => setFiltroClasificacion('contratista')}
              >
                Contratistas
              </button>
              <button
                className={`filtro-btn ${filtroClasificacion === 'administrativo' ? 'active' : ''}`}
                onClick={() => setFiltroClasificacion('administrativo')}
              >
                Administrativos
              </button>
            </div>
          </div>

          <div className="prov-filtro-grupo">
            <label>Tipo Retención:</label>
            <select
              value={filtroRetencion}
              onChange={(e) => setFiltroRetencion(e.target.value)}
              className="prov-filtro-select"
            >
              <option value="todos">Todos los tipos</option>
              {tiposRetencion.map(tipo => (
                <option key={tipo.codigo} value={tipo.codigo}>
                  {tipo.nombre} ({tipo.cantidad_proveedores})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Proveedores */}
      {proveedoresFiltrados.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🏢</span>
          <h3>No hay proveedores</h3>
          <p>
            {busqueda
              ? `No hay proveedores que coincidan con "${busqueda}"`
              : 'No se han registrado proveedores con estos filtros'}
          </p>
          <button className="btn-nuevo-empty" onClick={() => {
            setBusqueda('');
            setFiltroTipo('todos');
            setFiltroClasificacion('todos');
            setFiltroRetencion('todos');
          }}>
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="proveedores-lista">
          {proveedoresFiltrados.map(proveedor => {
            const tipoInfo = tiposProveedor[proveedor.tipo] || tiposProveedor.otro;
            const facturasPendientes = proveedor.facturas_pendientes_lista || [];
            const tieneRetencion = proveedor.tipo_retencion_nombre && proveedor.porcentaje_retencion > 0;

            return (
              <div
                key={proveedor.id}
                className="proveedor-card"
                onClick={() => onVerDetalle && onVerDetalle(proveedor.id)}
              >
                {/* Header del Proveedor */}
                <div className="proveedor-header">
                  <div className="proveedor-info">
                    <div>
                      <h4>{proveedor.nombre}</h4>
                      <div className="proveedor-badges">
                        <span className="proveedor-tipo-badge" style={{ background: tipoInfo.color }}>
                          {tipoInfo.nombre}
                        </span>
                        <span className={`proveedor-clasificacion-badge ${proveedor.clasificacion || 'proveedor'}`}>
                          {proveedor.clasificacion === 'contratista' ? 'Contratista' : proveedor.clasificacion === 'administrativo' ? 'Administrativo' : 'Proveedor'}
                        </span>
                        {proveedor.nit && (
                          <span className="proveedor-nit">NIT: {proveedor.nit}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="proveedor-stats-mini">
                    <div className="stat-mini">
                      <span className="stat-mini-value">{proveedor.facturas_pendientes}</span>
                      <span className="stat-mini-label">Pendientes</span>
                    </div>
                    <div className="stat-mini">
                      <span className="stat-mini-value">{formatMoney(proveedor.saldo_total_pendiente)}</span>
                      <span className="stat-mini-label">Saldo</span>
                    </div>
                  </div>
                </div>

                {/* Información de Retenciones Múltiples */}
                <div className={`proveedor-retencion ${proveedor.retenciones?.length > 0 ? 'configurada' : 'sin-configurar'}`}>
                  {proveedor.retenciones?.length > 0 ? (
                    <div className="retenciones-lista">
                      {proveedor.retenciones.map((ret, index) => (
                        <div key={index} className={`retencion-item ${ret.es_principal == 1 ? 'principal' : 'secundaria'}`}>
                          <span className="retencion-nombre">{ret.tipo_retencion_nombre}</span>
                          <span className="retencion-porcentaje">{formatPorcentaje(ret.porcentaje_retencion)}</span>
                          {ret.base_retencion > 0 && (
                            <span className="retencion-base">Base: {formatMoney(ret.base_retencion)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="sin-retencion">Sin retención configurada</span>
                  )}
                </div>

                {/* Contacto Rápido */}
                {(proveedor.telefono || proveedor.email) && (
                  <div className="proveedor-contacto">
                    {proveedor.telefono && <span>Tel: {proveedor.telefono}</span>}
                    {proveedor.email && <span>{proveedor.email}</span>}
                  </div>
                )}

                {/* Descuento pactado: no debe abrir el detalle de la tarjeta */}
                <button
                  className="prov-btn-descuento"
                  title="Registrar un descuento a este proveedor"
                  onClick={(e) => { e.stopPropagation(); setModalDescuento(proveedor); }}
                >
                  Registrar descuento
                </button>

                {/* Facturas Pendientes Mini */}
                {facturasPendientes.length > 0 && (
                  <div className="facturas-pendientes">
                    <h5>Facturas Pendientes ({facturasPendientes.length})</h5>
                    <div className="facturas-lista-mini">
                      {facturasPendientes.slice(0, 2).map(factura => (
                        <div key={factura.id} className={`factura-mini ${factura.dias_vencido > 0 ? 'vencida' : ''}`}>
                          <span className="factura-numero">#{factura.numero_factura}</span>
                          <span className="factura-monto">{formatMoney(factura.saldo_pendiente)}</span>
                          {factura.dias_vencido > 0 && <span className="vencido-badge">Vencida</span>}
                        </div>
                      ))}
                      {facturasPendientes.length > 2 && (
                        <span className="ver-mas">+{facturasPendientes.length - 2} más</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Sin facturas */}
                {proveedor.total_facturas === 0 && (
                  <div className="sin-facturas">Sin facturas registradas</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Modal Actualización Masiva de Bases */}
      {mostrarModalBases && (
        <div className="modal-overlay" onClick={() => setMostrarModalBases(false)}>
          <div className="modal-bases-masivas" onClick={e => e.stopPropagation()}>
            <div className="modal-bases-header">
              <h3>Actualizar Bases de Retención</h3>
              <button className="modal-close" onClick={() => setMostrarModalBases(false)}>✕</button>
            </div>
            <p className="modal-bases-desc">
              Actualiza la base de retención de forma masiva. Se aplicará a todos los proveedores que tengan configurada cada tipo de retención.
            </p>
            <div className="modal-bases-tabla">
              <table>
                <thead>
                  <tr>
                    <th>Tipo de Retención</th>
                    <th>Proveedores</th>
                    <th>% Actual</th>
                    <th>Nuevo %</th>
                    <th>Base Actual</th>
                    <th>Nueva Base</th>
                  </tr>
                </thead>
                <tbody>
                  {basesEdicion.map(tipo => (
                    <tr key={tipo.id}>
                      <td className="tipo-nombre">
                        <strong>{tipo.nombre}</strong>
                        <span className="tipo-codigo">{tipo.codigo}</span>
                      </td>
                      <td className="tipo-cant">{tipo.cantidad_proveedores}</td>
                      <td className="tipo-pct">{tipo.porcentaje}%</td>
                      <td className="tipo-pct-nuevo">
                        <input
                          type="number"
                          placeholder="Nuevo %"
                          value={tipo.porcentaje_nuevo}
                          onChange={e => handlePorcentajeChange(tipo.id, e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </td>
                      <td className="tipo-base-actual">
                        {tipo.base_actual > 0
                          ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(tipo.base_actual)
                          : '-'}
                      </td>
                      <td className="tipo-base-nueva">
                        <input
                          type="number"
                          placeholder="Nueva base..."
                          value={tipo.base_nueva}
                          onChange={e => handleBaseChange(tipo.id, e.target.value)}
                          min="0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-bases-footer">
              <button className="btn-cancelar" onClick={() => setMostrarModalBases(false)}>Cancelar</button>
              <button className="btn-guardar-bases" onClick={guardarBasesMasivas}>
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {modalDescuento && (
        <ModalDescuentoProveedor
          proveedor={modalDescuento}
          onCerrar={() => setModalDescuento(null)}
          onGuardado={cargarProveedores}
        />
      )}
    </div>
  );
};

export default Proveedores;
